import {Injectable} from "@angular/core";
import {CCRoles} from "../../../model/security/cc-roles-type";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {CustomerAccountDataLoadingService} from "./customer-account-data-loading.service";
import {AccountType} from "../../../model/exchange/account-type";
import {AccountComponent} from "../../../crystal-clear/customer/account/account.component";
import {AppConfigService} from "../../app-config.service";
import TYPE = FormFieldConfig.TYPE;
import {
  BatchActionAccountFormComponent
} from "../../../crystal-clear/customer/account/batch-update-form/batch-action-account-form.component";
import {BatchUtils} from "../../../utils/batch-utils";
import {
  BatchActionCustomerFormComponent
} from "../../../crystal-clear/customer/customer/batch-update-form/batch-action-customer-form.component";
import {AccountDefinition} from "../../../model/customer/account-definition";

@Injectable({
  providedIn: 'root'
})
export class CustomerAccountBatchFormConfigService {

  fieldsToUpdate: string[] = [];

  constructor(readonly appConfigService: AppConfigService,
              readonly dataLoadingService: CustomerAccountDataLoadingService) {
  }

  public loadConfig(component: BatchActionAccountFormComponent): FormFieldGroupConfig[] {

    const groupCustExhFields = [
      {
        field: 'owner',
        optionValue: 'text',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.owners,
        changed: event => {
          this.dataLoadingService.filterCustomers(component.formRecord);
          // this.dataLoadingService.refreshBrokerageTables(component.formRecord);
          // this.dataLoadingService.refreshMoneyOwners(component.formRecord);
        },
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => {
          BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked);
          this.validate(component);
        },
        disabled: (record: any) => !this.fieldsToUpdate.includes('owner'),
        validate: record => this.validate(component)
      },
      {
        field: 'customerId',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.displayedCustomers,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => {
          BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked);
          this.validate(component);
        },
        disabled: (record: any) => !this.fieldsToUpdate.includes('customerId'),
      },
      {
        type: TYPE.NONE,
        column: 6,
      },
      {
        field: 'clearingHouse',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.clearingHouses,
        changed: event => {
          this.dataLoadingService.filterAccountType(component.formRecord);
          this.dataLoadingService.filterExchanges(component.formRecord);
        },
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('clearingHouse'),
      },
      {
        field: 'exchangeCode',
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        icon: (id: string) => {
          if (this.dataLoadingService.exchangeCodes) {
            const exchField = this.dataLoadingService.exchangeCodes.find(exchange => exchange.value && exchange.value.code === id);
            if (exchField && exchField.value)
              return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
          }
          return 'fas fa-globe-europe';
        },
        options: () => this.dataLoadingService.displayedExchangeCodes,
        changed: event => {
          this.dataLoadingService.filterAccountType(component.formRecord);
        },
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('exchangeCode'),
      },
      {
        field: 'enabled',
        column: 2,
        type: TYPE.CHECKBOX,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('enabled'),
      }];
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupCustExhFields.push(
        {
          field: 'waitForApproval',
          column: 4,
          type: TYPE.CHECKBOX,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('waitForApproval'),
        },
        {
          field: 'allFutures',
          column: 3,
          type: TYPE.CHECKBOX,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('allFutures'),
        },
        {
          field: 'allOptions',
          column: 3,
          type: TYPE.CHECKBOX,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('allOptions'),
        },
        {
          type: TYPE.NONE,
          column: 6,
        },
        {
          field: 'contractList',
          column: 8,
          type: TYPE.TEXT,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('contractList'),
        },
        {
          field: 'contractListInclude',
          column: 4,
          type: TYPE.CHECKBOX,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('contractListInclude'),
        });
    }

    // const groupDetailsFields: FormFieldConfig[] = [{
    //   field: 'code',
    //   required: true,
    //   column: 1,
    //   // disabled: (record: any) => true
    //   type: TYPE.NONE,
    //   // iconValue: (record: any) => record.accountTypeCode && record.accountTypeCode.toUpperCase(),
    //   icon: id => '',
    //   iconValue: (id) => component.formRecord.code
    // },
    //   {
    //     field: 'accountTypeCode',
    //     required: true,
    //     filter: true,
    //     column: 2,
    //     type: TYPE.AUTOCOMPLETE,
    //     options: () => this.dataLoadingService.displayedAccountTypes,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('accountTypeCode'),
    //   },
    //   {
    //     field: 'traderName',
    //     column: 2,
    //     filter: true,
    //     type: TYPE.AUTOCOMPLETE,
    //     options: () => component.getDistinctOpts(this.dataLoadingService.traders),
    //     changed: event => {
    //       component.formRecord.traderName = event.text;
    //     }                                                ,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('traderName'),
    //   }];
    // if (this.appConfigService.iceTraderMemoFeature) {
    //   groupDetailsFields.push(
    //     {
    //       field: 'bindTraderToMemo',
    //       column: 2,
    //       type: TYPE.CHECKBOX,
    //       labelCheckbox: true,
    //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //       disabled: (record: any) => !this.fieldsToUpdate.includes('bindTraderToMemo'),
    //
    //     })
    // }
    // if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
    //   groupDetailsFields.push(
    //     {
    //       field: 'executionFee',
    //       column: 1,
    //       type: TYPE.TEXT  ,
    //       labelCheckbox: true,
    //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //       disabled: (record: any) => !this.fieldsToUpdate.includes('executionFee'),
    //     },
    //     {
    //       field: 'moneyOwner',
    //       column: 2,
    //       type: TYPE.DROPDOWN,
    //       options: () => this.dataLoadingService.moneyOwnersVisibles ,
    //       labelCheckbox: true,
    //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //       disabled: (record: any) => !this.fieldsToUpdate.includes('moneyOwner'),
    //     },
    //     {
    //       field: 'agreementId',
    //       column: 2,
    //       type: TYPE.DROPDOWN,
    //       options: () => this.dataLoadingService.brokerageTablesVisibles         ,
    //       labelCheckbox: true,
    //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //       disabled: (record: any) => !this.fieldsToUpdate.includes('agreementId'),
    //     }
    //   );
    // }
    // const groupGiveupFields: FormFieldConfig[] = [
    //   {
    //     field: 'giveUpBrokerCode',
    //     required: true,
    //     column: 2,
    //     type: TYPE.TEXT              ,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpBrokerCode'),
    //   },
    //   {
    //     field: 'giveUpMessage',
    //     required: true,
    //     column: 2,
    //     type: TYPE.TEXT              ,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpMessage'),
    //   },
    //   {
    //     field: 'giveupAccountType',
    //     filter: true,
    //     column: 2,
    //     type: TYPE.AUTOCOMPLETE,
    //     options: () => this.dataLoadingService.displayedGUpAccountTypes,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('giveupAccountType'),
    //   },
    //   {
    //     field: 'giveupMessage2',
    //     column: 2,
    //     type: TYPE.TEXT         ,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('giveupMessage2'),
    //   },
    //   {
    //     field: 'giveupMessage3',
    //     column: 2,
    //     type: TYPE.TEXT     ,
    //     labelCheckbox: true,
    //     labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //     disabled: (record: any) => !this.fieldsToUpdate.includes('giveupMessage3'),
    //   }
    // ];
    // if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
    //   groupGiveupFields.push(
    //     {
    //       field: 'giveUpCommission',
    //       column: 2,
    //       type: TYPE.TEXT           ,
    //       labelCheckbox: true,
    //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
    //       disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpCommission'),
    //     }
    //   );
    // }
    const formConfig = [
      {
        group: 'customer_exchange',
        fields: groupCustExhFields
      },
      // {
      //   group: 'details',
      //   fields: groupDetailsFields
      // },
      // {
      //   group: 'clearing',
      //   hidden: record => AccountType.GIVE_UP_CODE === record.accountTypeCode,
      //   fields: [
      //     {
      //       field: 'reference1',
      //       required: true,
      //       column: 2,
      //       type: TYPE.TEXT      ,
      //       labelCheckbox: true,
      //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
      //       disabled: (record: any) => !this.fieldsToUpdate.includes('reference1'),
      //     },
      //     {
      //       field: 'reference2',
      //       column: 2,
      //       type: TYPE.TEXT         ,
      //       labelCheckbox: true,
      //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
      //       disabled: (record: any) => !this.fieldsToUpdate.includes('reference2'),
      //     },
      //     {
      //       field: 'reference3',
      //       column: 2,
      //       type: TYPE.TEXT,
      //       labelCheckbox: true,
      //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
      //       disabled: (record: any) => !this.fieldsToUpdate.includes('reference3'),
      //     },
      //     {
      //       field: 'openCloseForFutures',
      //       filter: true,
      //       column: 2,
      //       type: TYPE.DROPDOWN,
      //       options: () => this.dataLoadingService.openCloses,
      //       labelCheckbox: true,
      //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
      //       disabled: (record: any) => !this.fieldsToUpdate.includes('openCloseForFutures'),
      //     },
      //     {
      //       field: 'openCloseForOptions',
      //       filter: true,
      //       column: 2,
      //       type: TYPE.DROPDOWN,
      //       options: () => this.dataLoadingService.openCloses,
      //       labelCheckbox: true,
      //       labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
      //       disabled: (record: any) => !this.fieldsToUpdate.includes('openCloseForOptions'),
      //     },
      //   ]
      // },
      // {
      //   group: 'giveup',
      //   hidden: record => AccountType.GIVE_UP_CODE != record.accountTypeCode,
      //   fields: groupGiveupFields,
      //   labelCheckbox: true,
      //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, component.ccFormDialog, event.field, event2.checked),
      //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveup'),
      // }

    ];

    return formConfig;
  }

  validate(component: BatchActionAccountFormComponent): string {
    setTimeout(() => {
      component.globalInfoMessage = !this.fieldsToUpdate.includes('customerId')
      && component.originOwner != component.formRecord.owner ?
        'The Customers will be updated/created according to the new Owner.' : '';

    })
    return '';
  }

}
